import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faUser,
    faPhone,
    faEnvelope,
    faMapMarkerAlt,
    faBuilding,
    faList,
    faGlobe,
    faUserTag,
    faStickyNote,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationTriangle,
    faStore,
    faExchangeAlt,
    faMoneyBillWave,
    faCreditCard,
    faFileAlt,
    faCopy,
    faTrashAlt
} from '@fortawesome/free-solid-svg-icons';
import "./Styles/Form.css";
import "../styles/LanguageTypography.css";

// ============================================================
// ONLY ENGLISH AND URDU LANGUAGES
// ============================================================
const RTL_LANGUAGES = new Set(['ur']);
const SUPPORTED_LANGUAGES = new Set(['en', 'ur']);

const normalizeLanguage = (lang) => {
    if (!lang) return 'en';
    const normalized = lang.toLowerCase().split('-')[0];
    return SUPPORTED_LANGUAGES.has(normalized) ? normalized : 'en';
};

// ============================================================
// HARDCODED BILINGUAL UI DICTIONARY - ENGLISH & URDU ONLY
// ============================================================
const UI = {
    en: {
        addSupplier: 'Add Supplier',
        fillDetails: 'Fill in the supplier details below',
        supplierName: 'Supplier Name',
        phoneNumber: 'Phone Number',
        email: 'Email Address',
        address: 'Address',
        company: 'Company',
        website: 'Website',
        contactPerson: 'Contact Person',
        notes: 'Notes',
        openingBalance: 'Opening Balance',
        addSupplierButton: 'Save Supplier',
        clearButton: 'Clear',
        adding: 'Adding...',
        supplierAdded: 'Supplier saved successfully!',
        failedAdd: 'Failed to save supplier',
        networkError: 'Network error occurred',
        nameRequired: 'Supplier name is required',
        nameTooShort: 'Name must be at least 2 characters',
        phoneInvalid: 'Please enter a valid phone number',
        emailInvalid: 'Please enter a valid email address',
        websiteInvalid: 'Please enter a valid website URL',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        selectShop: 'Select a shop first',
        noShopSelected: 'No shop selected',
        switchShop: 'Switch Shop',
        shopSwitched: 'Shop switched successfully',
        shopSwitchFailed: 'Failed to switch shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        optional: 'Optional',
        draftLoaded: 'Draft loaded',
        draftCleared: 'Draft cleared',
        clearForm: 'Clear Form',
        saveDraft: 'Save Draft',
        drafts: 'Drafts',
        savedDrafts: 'Saved Drafts',
        unnamedSupplier: 'Unnamed Supplier',
        noDraftsSaved: 'No drafts saved',
        save: 'Save',
        goBack: 'Go Back',
        viewDrafts: 'View Drafts',
        cannotSaveEmptyDraft: 'Cannot save an empty draft',
        draftSaved: 'Draft saved successfully!',
        draftDeleted: 'Draft deleted successfully!',
        loginUnavailable: 'Your login username is unavailable. Please sign in again.',
        noCategoriesFound: 'No categories found',
        switchShopTooltip: 'Switch Shop',
        clearFormTooltip: 'Clear Form',
        saveDraftTooltip: 'Save as Draft',
        viewDraftsTooltip: 'View Drafts',
        unnamed: 'Unnamed',
        draftExists: 'A saved draft exists',
        shopName: 'Shop',
        loadingUserData: 'Loading...',
    },
    ur: {
        addSupplier: 'سپلائر شامل کریں',
        fillDetails: 'سپلائر کی تفصیلات نیچے بھریں',
        supplierName: 'سپلائر کا نام',
        phoneNumber: 'فون نمبر',
        email: 'ای میل',
        address: 'پتہ',
        company: 'کمپنی',
        website: 'ویب سائٹ',
        contactPerson: 'رابطہ شخص',
        notes: 'نوٹس',
        openingBalance: 'ابتدائی بیلنس',
        addSupplierButton: 'سپلائر محفوظ کریں',
        clearButton: 'صاف کریں',
        adding: 'شامل ہو رہا ہے...',
        supplierAdded: 'سپلائر کامیابی سے محفوظ ہو گیا!',
        failedAdd: 'سپلائر محفوظ نہیں ہو سکا',
        networkError: 'نیٹ ورک کی خرابی',
        nameRequired: 'سپلائر کا نام ضروری ہے',
        nameTooShort: 'نام کم از کم 2 حروف کا ہونا چاہیے',
        phoneInvalid: 'درست فون نمبر درج کریں',
        emailInvalid: 'درست ای میل درج کریں',
        websiteInvalid: 'درست ویب سائٹ URL درج کریں',
        success: 'کامیابی',
        error: 'خرابی',
        warning: 'انتباہ',
        selectShop: 'پہلے دکان منتخب کریں',
        noShopSelected: 'کوئی دکان منتخب نہیں',
        switchShop: 'دکان تبدیل کریں',
        shopSwitched: 'دکان کامیابی سے تبدیل ہو گئی',
        shopSwitchFailed: 'دکان تبدیل نہیں ہو سکی',
        loadingShops: 'دکانیں لوڈ ہو رہی ہیں...',
        noShops: 'کوئی دکان دستیاب نہیں',
        current: 'موجودہ',
        optional: '',
        draftLoaded: 'ڈرافٹ لوڈ ہو گیا',
        draftCleared: 'ڈرافٹ صاف ہو گیا',
        clearForm: 'فارم صاف کریں',
        saveDraft: 'ڈرافٹ محفوظ کریں',
        drafts: 'ڈرافٹس',
        savedDrafts: 'محفوظ ڈرافٹس',
        unnamedSupplier: 'بے نام سپلائر',
        noDraftsSaved: 'کوئی ڈرافٹ محفوظ نہیں',
        save: 'محفوظ کریں',
        goBack: 'واپس جائیں',
        viewDrafts: 'ڈرافٹس دیکھیں',
        cannotSaveEmptyDraft: 'خالی ڈرافٹ محفوظ نہیں ہو سکتا',
        draftSaved: 'ڈرافٹ کامیابی سے محفوظ ہو گیا!',
        draftDeleted: 'ڈرافٹ کامیابی سے حذف ہو گیا!',
        loginUnavailable: 'لاگ ان نام دستیاب نہیں۔ دوبارہ سائن ان کریں۔',
        noCategoriesFound: 'کوئی کیٹیگری نہیں ملی',
        switchShopTooltip: 'دکان تبدیل کریں',
        clearFormTooltip: 'فارم صاف کریں',
        saveDraftTooltip: 'ڈرافٹ محفوظ کریں',
        viewDraftsTooltip: 'ڈرافٹس دیکھیں',
        unnamed: 'بے نام',
        draftExists: 'پہلے سے محفوظ ڈرافٹ موجود ہے',
        shopName: 'دکان',
        loadingUserData: 'لوڈ ہو رہا ہے...',
    }
};

// ============================================================
// TOAST NOTIFICATION - POSITION BASED ON LANGUAGE
// ============================================================
const ToastNotification = ({ toast, setToast, t, isRTL }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return t.success || 'Success';
            case 'error': return t.error || 'Error';
            case 'warning': return t.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const positionStyle = isRTL
        ? { bottom: '20px', left: '20px', right: 'auto' }
        : { bottom: '20px', right: '20px', left: 'auto' };

    return (
        <div className={`ozui_toast-notification ${toast.type}`} style={positionStyle}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ============================================================
// SHOP SELECTOR MODAL
// ============================================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, t, loading, isRTL }) => {
    if (!isOpen) return null;

    return (
        <div className="ozui_modal-overlay" onClick={onClose}>
            <div className="ozui_modal" onClick={e => e.stopPropagation()} style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <div className="ozui_modal-header">
                    <h3>{t.switchShop}</h3>
                    <button className="ozui_modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozui_modal-body">
                    {loading ? (
                        <div className="ozui_loading-state">{t.loadingShops}</div>
                    ) : shops.length === 0 ? (
                        <div className="ozui_empty-state">{t.noShops}</div>
                    ) : (
                        shops.map((shop) => (
                            <div 
                                key={shop.shop_id}
                                className={`ozui_shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozui_active' : ''}`}
                                onClick={() => onSelectShop(shop)}
                            >
                                <div className="ozui_shop-info">
                                    <div className="ozui_shop-name">{shop.shop_name}</div>
                                    <div className="ozui_shop-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                </div>
                                {currentShop?.shop_id === shop.shop_id && (
                                    <div className="ozui_shop-badge">✓ {t.current}</div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// MAIN SUPPLIER COMPONENT
// ============================================================
const AddSupplier = () => {
    const auth = useAuth();
    const { user, currentShop, switchShop, getShopsForSwitch } = auth;
    
    const supplierNameInputRef = useRef(null);
    
    // ============================================================
    // LANGUAGE DETECTION
    // ============================================================
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return normalizeLanguage(
            auth?.currentLanguage || 
            auth?.language || 
            user?.language || 
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') || 
            'en'
        );
    });

    const isRTL = currentLanguage === 'ur';
    const t = UI[currentLanguage] || UI.en;

    const initialFormData = {
        supplier_name: '',
        phone: '',
        email: '',
        address: '',
        company: '',
        website: '',
        contact_person: '',
        notes: '',
        opening_balance: '',
    };
    
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [darkMode, setDarkMode] = useState(() => {
        return document.body.classList.contains('dark-mode') || localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    
    const isSubmittingRef = useRef(false);
    const toastTimerRef = useRef(null);
    const formRef = useRef(null);
    const submitButtonRef = useRef(null);
    const draftsDropdownRef = useRef(null);

    // ============================================================
    // AUTO-FOCUS
    // ============================================================
    useEffect(() => {
        if (supplierNameInputRef.current && window.innerWidth > 600) {
            supplierNameInputRef.current.focus();
        }
    }, []);

    // ============================================================
    // DRAFT MANAGEMENT
    // ============================================================
    const saveDraft = () => {
        if (!formData.supplier_name && !formData.phone && !formData.email) {
            showToast('warning', t.cannotSaveEmptyDraft);
            return;
        }
        
        const draftId = Date.now();
        const draft = {
            id: draftId,
            ...formData,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id,
            language: currentLanguage
        };
        
        const existingDrafts = JSON.parse(localStorage.getItem('supplier_drafts') || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem('supplier_drafts', JSON.stringify(limitedDrafts));
        
        showToast('success', t.draftSaved);
        loadDrafts();
    };

    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('supplier_drafts') || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    const loadDraft = (draft) => {
        setFormData({
            supplier_name: draft.supplier_name || '',
            phone: draft.phone || '',
            email: draft.email || '',
            address: draft.address || '',
            company: draft.company || '',
            website: draft.website || '',
            contact_person: draft.contact_person || '',
            notes: draft.notes || '',
            opening_balance: draft.opening_balance || '',
        });
        setShowDraftsList(false);
        showToast('success', t.draftLoaded);
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem('supplier_drafts') || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('supplier_drafts', JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', t.draftDeleted);
    };

    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    // ============================================================
    // CLICK OUTSIDE HANDLERS
    // ============================================================
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (draftsDropdownRef.current && !draftsDropdownRef.current.contains(event.target)) {
                setShowDraftsList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ============================================================
    // TOAST HELPER
    // ============================================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ============================================================
    // THEME COLOR
    // ============================================================
    const getActiveThemeColor = () => {
        const rootStyle = getComputedStyle(document.documentElement);
        const docPrimary = rootStyle.getPropertyValue('--primary-color').trim();
        if (docPrimary && /^#[0-9A-F]{6}$/i.test(docPrimary)) {
            return docPrimary;
        }
        const storedTheme = localStorage.getItem('ourzest_theme');
        if (storedTheme && /^#[0-9A-F]{6}$/i.test(storedTheme)) {
            return storedTheme;
        }
        return '#6366f1';
    };

    const applyTheme = useCallback((color) => {
        const activeColor = color || getActiveThemeColor();
        document.documentElement.style.setProperty('--ozui-primary', activeColor);
        document.documentElement.style.setProperty('--ozui-primary-dark', activeColor);
        document.documentElement.style.setProperty('--ozui-primary-light', activeColor);
        
        const updateOrbColors = () => {
            let r, g, b;
            if (activeColor.startsWith('#')) {
                r = parseInt(activeColor.slice(1, 3), 16);
                g = parseInt(activeColor.slice(3, 5), 16);
                b = parseInt(activeColor.slice(5, 7), 16);
            } else {
                r = 99; g = 102; b = 241;
            }
            
            let style = document.getElementById('dynamic-orb-styles-supplier');
            if (!style) {
                style = document.createElement('style');
                style.id = 'dynamic-orb-styles-supplier';
                document.head.appendChild(style);
            }
            
            style.textContent = `
                .ozui-header-bg-animation .gradient-orb.orb-1 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-2 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-3 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-4 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
                }
                .ozui-header-bg-animation .gradient-orb.orb-5 {
                    background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
                }
            `;
        };
        
        updateOrbColors();
        setCurrentThemeColor(activeColor);
    }, []);

    useEffect(() => {
        const syncTheme = () => {
            const activeColor = getActiveThemeColor();
            applyTheme(activeColor);
        };

        syncTheme();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') syncTheme();
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        const themeInterval = setInterval(syncTheme, 300);

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(themeInterval);
        };
    }, [applyTheme]);

    // ============================================================
    // DARK MODE SYNC
    // ============================================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isBodyDark = document.body.classList.contains('dark-mode') || document.body.classList.contains('ozui-dark');
            const isStorageDark = localStorage.getItem('ourzest_darkMode') === 'true';
            const shouldBeDark = isBodyDark || isStorageDark;

            setDarkMode(shouldBeDark);
            
            const container = document.querySelector('.ozui-container');
            if (container) {
                if (shouldBeDark) container.classList.add('ozui-dark');
                else container.classList.remove('ozui-dark');
            }
        };
        
        syncDarkMode();

        const observer = new MutationObserver(() => {
            syncDarkMode();
        });

        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') syncDarkMode();
        };
        
        window.addEventListener('storage', handleStorageChange);
        return () => {
            observer.disconnect();
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // ============================================================
    // LANGUAGE SYNC
    // ============================================================
    useEffect(() => {
        const contextLanguage = normalizeLanguage(
            auth?.currentLanguage || 
            auth?.language || 
            user?.language || 
            user?.preferred_language ||
            localStorage.getItem('ourzest_language') || 
            'en'
        );
        
        if (contextLanguage !== currentLanguage) {
            setCurrentLanguage(contextLanguage);
        }
    }, [auth?.currentLanguage, auth?.language, user?.language, user?.preferred_language]);

    useEffect(() => {
        const handleLanguageChange = (event) => {
            const newLang = normalizeLanguage(event?.detail?.language || event?.detail?.lang);
            if (newLang && newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
            }
        };

        window.addEventListener('languageChanged', handleLanguageChange);
        window.addEventListener('ourzest:language-change', handleLanguageChange);
        window.addEventListener('storage', (e) => {
            if (e.key === 'ourzest_language') {
                const newLang = normalizeLanguage(e.newValue);
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                }
            }
        });

        return () => {
            window.removeEventListener('languageChanged', handleLanguageChange);
            window.removeEventListener('ourzest:language-change', handleLanguageChange);
        };
    }, [currentLanguage]);

    // ============================================================
    // RTL DIRECTION
    // ============================================================
    useEffect(() => {
        document.documentElement.lang = currentLanguage;
        document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
        document.body.dir = isRTL ? 'rtl' : 'ltr';
        const container = document.querySelector('.ozui-container');
        if (container) {
            if (isRTL) {
                container.classList.add('ozui-rtl');
                container.classList.remove('ozui-ltr');
            } else {
                container.classList.add('ozui-ltr');
                container.classList.remove('ozui-rtl');
            }
        }
    }, [currentLanguage, isRTL]);

    // ============================================================
    // SUBMIT SUPPLIER
    // ============================================================
    const submitSupplier = async (submitData) => {
        if (isSubmittingRef.current) return false;
        isSubmittingRef.current = true;

        const loggedInUsername = String(
            user?.username || user?.user_name || user?.login_username || ''
        ).trim();
        
        if (!loggedInUsername) {
            showToast('error', t.loginUnavailable);
            isSubmittingRef.current = false;
            return false;
        }

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'add',
                    supplier_name: submitData.supplier_name,
                    phone: submitData.phone,
                    email: submitData.email,
                    address: submitData.address,
                    company: submitData.company,
                    website: submitData.website,
                    contact_person: submitData.contact_person,
                    notes: submitData.notes,
                    opening_balance: submitData.opening_balance,
                    language: currentLanguage,
                    username: loggedInUsername,
                    shop_id: currentShop.shop_id,
                    shop_name: currentShop.shop_name,
                    shop_code: currentShop.shop_code
                })
            });

            const raw = await response.text();
            let data;
            try {
                data = raw ? JSON.parse(raw.replace(/^\uFEFF/, '')) : null;
            } catch (_) {
                throw new Error('Server returned invalid JSON');
            }

            if (!response.ok) throw new Error(data?.message || `Server error: ${response.status}`);
            if (!data) throw new Error('Server returned an empty response');

            if (data.status === 'success') {
                showToast('success', t.supplierAdded);
                setFormData(initialFormData);
                setErrors({});
                return true;
            } else {
                showToast('error', data.message || t.failedAdd);
                return false;
            }
        } catch (error) {
            console.error('Supplier submit error:', error);
            showToast('error', t.networkError);
            return false;
        } finally {
            setTimeout(() => {
                isSubmittingRef.current = false;
                setLoading(false);
            }, 1000);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmittingRef.current) return;
        if (!currentShop) {
            showToast('error', t.selectShop);
            setShowShopSelector(true);
            return;
        }
        if (!validateForm()) return;
        
        setLoading(true);
        await submitSupplier(formData);
        setLoading(false);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.supplier_name.trim()) {
            newErrors.supplier_name = t.nameRequired;
        } else if (formData.supplier_name.trim().length < 2) {
            newErrors.supplier_name = t.nameTooShort;
        }
        if (formData.phone && !/^[\d\s\-\+\(\)]{10,15}$/.test(formData.phone)) {
            newErrors.phone = t.phoneInvalid;
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+\s*$/.test(formData.email)) {
            newErrors.email = t.emailInvalid;
        }
        if (formData.website) {
            try {
                new URL(formData.website);
            } catch {
                newErrors.website = t.websiteInvalid;
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleClear = () => {
        setFormData(initialFormData);
        setErrors({});
        showToast('success', t.draftCleared);
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', t.shopSwitched);
                setShowShopSelector(false);
            } else {
                showToast('error', t.shopSwitchFailed);
            }
        } catch (error) {
            showToast('error', t.shopSwitchFailed);
        }
    };

    useEffect(() => {
        const loadAvailableShops = async () => {
            if (!user) return;
            setLoadingShops(true);
            try {
                const shopsData = await getShopsForSwitch();
                setAvailableShops(shopsData.shops || []);
            } catch (error) {
                setAvailableShops([]);
            } finally {
                setLoadingShops(false);
            }
        };
        loadAvailableShops();
    }, [user, getShopsForSwitch]);

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className={`ozui-container ${isRTL ? 'ozui-rtl' : 'ozui-ltr'} ${darkMode ? 'ozui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} t={t} isRTL={isRTL} />
            
            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                t={t}
                loading={loadingShops}
                isRTL={isRTL}
            />

            {/* Sticky Top Header */}
            <div className="ozui-sticky-header">
                <div className="ozui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozui-header-content">
                    <div className="ozui-header-left">
                        <div className="ozui-header-title">
                            <h1>{t.addSupplier}</h1>
                            <p>{t.fillDetails}</p>
                        </div>
                    </div>
                    
                    <div className="ozui-header-right">
                        {!currentShop && (
                            <button type="button" className="ozui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{t.switchShop}</span>
                            </button>
                        )}
                        
                        <div className="ozui-header-actions">
                            <button 
                                type="button"
                                className="ozui-action-btn clear-draft" 
                                onClick={handleClear}
                                disabled={loading}
                                title={t.clearFormTooltip}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{t.clearButton}</span>
                            </button>
                            
                            <button 
                                type="button"
                                className="ozui-action-btn save-draft" 
                                onClick={saveDraft}
                                disabled={loading}
                                title={t.saveDraftTooltip}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{t.saveDraft}</span>
                            </button>
                            
                            <div className="ozui-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button 
                                    type="button"
                                    className="ozui-action-btn drafts-btn" 
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={t.viewDraftsTooltip}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{t.drafts}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                
                                {showDraftsList && (
                                    <div className="ozui-drafts-dropdown" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                                        <div className="drafts-header">
                                            <h4>{t.savedDrafts}</h4>
                                            <button type="button" onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{t.noDraftsSaved}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.supplier_name || t.unnamed}
                                                            </div>
                                                            <div className="draft-details">
                                                                {draft.phone && <span>{draft.phone}</span>}
                                                                {draft.company && <span>{draft.company}</span>}
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            type="button"
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <form ref={formRef} onSubmit={handleSubmit} className="ozui-form" autoCapitalize="sentences">
                <div className="ozui-form-row">
                    <div className={`ozui-field ${errors.supplier_name ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                ref={supplierNameInputRef}
                                type="text"
                                name="supplier_name"
                                id="supplier_name"
                                value={formData.supplier_name}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="name"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="supplier_name">
                                <FontAwesomeIcon icon={faUser} />
                                {t.supplierName} <span>*</span>
                            </label>
                        </div>
                        {errors.supplier_name && <small>{errors.supplier_name}</small>}
                    </div>

                    <div className={`ozui-field ${errors.phone ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="tel"
                                inputMode="tel"
                                name="phone"
                                id="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="tel"
                                dir="ltr"
                            />
                            <label htmlFor="phone">
                                <FontAwesomeIcon icon={faPhone} />
                                {t.phoneNumber} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.phone && <small>{errors.phone}</small>}
                    </div>

                    <div className={`ozui-field ${errors.email ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="email"
                                inputMode="email"
                                name="email"
                                id="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="email"
                                dir="ltr"
                            />
                            <label htmlFor="email">
                                <FontAwesomeIcon icon={faEnvelope} />
                                {t.email} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.email && <small>{errors.email}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="company"
                                id="company"
                                value={formData.company}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                autoComplete="organization"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="company">
                                <FontAwesomeIcon icon={faBuilding} />
                                {t.company} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="contact_person"
                                id="contact_person"
                                value={formData.contact_person}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="contact_person">
                                <FontAwesomeIcon icon={faUserTag} />
                                {t.contactPerson} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="text"
                                name="address"
                                id="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="address">
                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                {t.address} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                    </div>

                    <div className={`ozui-field ${errors.website ? 'ozui-error' : ''}`}>
                        <div className="ozui-input-wrapper">
                            <input
                                type="url"
                                inputMode="url"
                                name="website"
                                id="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir="ltr"
                            />
                            <label htmlFor="website">
                                <FontAwesomeIcon icon={faGlobe} />
                                {t.website} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                        {errors.website && <small>{errors.website}</small>}
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                name="opening_balance"
                                id="opening_balance"
                                value={formData.opening_balance}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                dir="ltr"
                            />
                            <label htmlFor="opening_balance">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                {t.openingBalance} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                    </div>

                    <div className="ozui-field">
                        <div className="ozui-input-wrapper">
                            <textarea
                                name="notes"
                                id="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder=" "
                                disabled={loading}
                                rows="3"
                                dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <label htmlFor="notes">
                                <FontAwesomeIcon icon={faStickyNote} />
                                {t.notes} 
                                {!isRTL && <span> ({t.optional})</span>}
                            </label>
                        </div>
                    </div>
                </div>
                
                {/* Submit Action */}
                <div className="ozui-form-actions">
                    <button 
                        ref={submitButtonRef}
                        type="submit" 
                        className="ozui-btn ozui-btn-primary" 
                        disabled={loading || !currentShop}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{t.adding}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faSave} />
                                <span>{t.addSupplierButton}</span>
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddSupplier;